import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CommentEntityType, CommentThreadType } from '../../../../api/comments';
import '../../../../app/markdown_editor.css';
import { useAppDispatch, useAppSelector } from '../../../../app/store';
import ActionButtonV2 from '../../../../components/ui/button/v2/ActionButton';
import { SaveButtonV2 } from '../../../../components/ui/button/v2/SaveButton';
import RedirectActionsBar from '../../../../components/ui/cards/RedirectActionsBar';
import { SelectionContainer } from '../../../../components/ui/dropdowns/SelectionContainer';
import CansoEditor from '../../../../components/ui/editor';
import { ReportSkeleton } from '../../../../components/ui/loading/ReportSkeleton';
import {
  upsertPartiesInvolvedThunk
} from '../../../../features/partiesinvolved/logic/PartiesInvolvedActionsThunk';
import { formatDateForDisplay, getCurrentDate } from '../../../../lib/utils';
import { handleSendEmail } from '../../../../services/mailtoGenerator';
import { renderFICEmailBody } from '../../../../services/templating';
import CommentMainPage from '../../../comments/components/CommentMainPage';
import {
  PartiesInvolvedSkeletonLoader
} from '../../../partiesinvolved/components/PartiesInvolvedSidebar';
import {
  selectAllPartiesInvolved,
  selectPartiesInvolvedStatus
} from '../../../partiesinvolved/logic/PartiesInvolvedSelectors';
import { selectFICReportState } from '../../logic/reportsSlice';
import { fetchFICReportThunk, updateFICReportThunk } from '../../logic/reportsThunk';
import ReportHeader from '../CommonReportHeader';
import { downloadFormats } from '../scn/components/utils';
import styles from './FICPage.module.css';
import { FICPartyInvolvedCard } from './FICPartyCard';
import { CaseRouteId, getCaseRoutePath } from '../../../caselayout/config/caseRoutes';


export const FICPage : React.FC = () => {

    const dispatch = useAppDispatch();
    const { id: case_id } = useParams<{ id: string }>();

    const ficReportState = useAppSelector(selectFICReportState);
    const isFicLoading = ficReportState.status === "loading"

    const parties = useAppSelector(selectAllPartiesInvolved);
    const partiesStatus = useAppSelector(selectPartiesInvolvedStatus);
    const isPartiesLoading = partiesStatus === "loading"

    const [commiteeRemarks, setCommiteeRemarks] = useState<string>('');
    const [fraudClassification, setFraudClassification] = useState<string>('');
    const [editorResetKey, setEditorResetKey] = useState(0);

    const [partyChanges, setPartyChanges] = useState<{
      [party_id: string]: {
        is_fraud?: boolean;
        generate_ro?: boolean;
      }
    }>({});

    const [saveEnabled, setSaveEnabled] = useState(false);

    const shouldDisableButtons = !commiteeRemarks || isPartiesLoading || isFicLoading;

    const fraudClassificationOptions: string[] = [
      'Misappropriation of funds and criminal breach of trust',
      'Fraudulent encashment through forged instruments',
      'Manipulation of books of accounts or through fictitious accounts, and conversion of property',
      'Cheating by concealment of facts with the intention to deceive any person and cheating by impersonation',
      'Forgery with the intention to commit fraud by making any false documents/electronic records',
      'Wilful falsification, destruction, alteration, mutilation of any book, electronic record, paper, writing, valuable security or account with intent to defraud',
      'Fraudulent credit facilities extended for illegal gratification',
      'Cash shortages on account of frauds',
      'Fraudulent transactions involving foreign exchange',
      'Fraudulent electronic banking / digital payment related transactions committed on NBFCs',
      'Other type of fraudulent activity not covered under any of the above.', 'No Fraud'
    ]

    useEffect(() => {
      setCommiteeRemarks(ficReportState.review_committee_remarks || '');
      setFraudClassification(ficReportState.fraud_classification || '');
      setEditorResetKey(prev => prev + 1);
    }, [ficReportState.review_committee_remarks, ficReportState.fraud_classification]);

    useEffect(() => {
        if (case_id) {
            dispatch(fetchFICReportThunk({case_id}));
        }
    }, [case_id, dispatch]);

    useEffect(() => {
      setSaveEnabled(
        commiteeRemarks !== ficReportState.review_committee_remarks ||
        fraudClassification !== ficReportState.fraud_classification ||
        Object.keys(partyChanges).length > 0
      );
    }, [commiteeRemarks, fraudClassification, ficReportState, partyChanges]);

    const handleFraudStatusChange = (partyId: string, value: "fraud" | "not-fraud") => {
      setPartyChanges(prev => ({
        ...prev,
        [partyId]: {
          ...prev[partyId],
          is_fraud: value === "fraud"
        }
      }));
    };

    const handleGenerateROChange = (partyId: string, checked: boolean) => {
      setPartyChanges(prev => ({
        ...prev,
        [partyId]: {
          ...prev[partyId],
          generate_ro: checked
        }
      }));
    };

    const handleSave = async () => {
      if (case_id) {
        try {
          // 1. Remarks
          if (commiteeRemarks !== ficReportState.review_committee_remarks ||
              fraudClassification !== ficReportState.fraud_classification) {
            await dispatch(updateFICReportThunk({
              case_id: case_id,
              request: {
                review_committee_remarks: commiteeRemarks,
                fraud_classification: fraudClassification
              }
            }));
          }

          // 2. Parties involved
          if (Object.keys(partyChanges).length > 0) {
            const partiesArray = Object.entries(partyChanges).map(([party_id, changes]) => {
              const party = parties.find(p => p.party_id === party_id);

              return {
                party_id,
                name: party?.name, // Include party name
                ...changes
              };
            });

            await dispatch(upsertPartiesInvolvedThunk({
              case_id: case_id,
              request: { parties_involved: partiesArray }
            }));
          }

        } catch (error) {
          console.error('Save failed:', error);
        }
      }
    };

    const handleSendEmailClick = async () => {
        if (!commiteeRemarks.trim()) return;

        const subject = `Fraud Classification Report - ${formatDateForDisplay(getCurrentDate())}`;
        const signature = "Best regards,\nFraud Review Committee";

        const emailBody = await renderFICEmailBody(commiteeRemarks, fraudClassification, signature);
        handleSendEmail(subject, emailBody);
    };

    if (isFicLoading) {
      return <ReportSkeleton />;
    }

    return (
      <>
        {/* Top header with Save & Navigation Breadcrumb - Outside the main container */}
        <div className={`${styles.topHeader} flex items-center justify-end`}>

          <SaveButtonV2
            disabled={!saveEnabled}
            onClick={handleSave}
            buttonText={
              ficReportState.status === "saving" || partiesStatus === "saving"
                ? "Saving..."
                : "Save"
            }
          />
        </div>

        <div className={styles.pageContainer}>
          <div className={styles.mainContent}>
            <div className={styles.reportCard}>
              {/* Report Header - takes full width of card */}
              <ReportHeader
                partyName="Fraud Classification Report"
                partyType="All Parties"
                rightContent={
                  <>
                    <ActionButtonV2
                      variant="secondary"
                      size="md"
                      actionType="EMAIL"
                      text="Send Email"
                      disabled={shouldDisableButtons}
                      onClick={handleSendEmailClick}
                    />
                    <ActionButtonV2
                      variant="secondary"
                      size="md"
                      actionType="DROPDOWN"
                      text="Download"
                      disabled={shouldDisableButtons}
                      options={downloadFormats}
                      onSelect={console.log}
                    />
                  </>
                }
              />

              {/* Content with left/right spacing */}
              <div className={styles.reportContent}>
                {/* Text Container */}
                <div className={styles.textFieldContainer}>
                  <span className={styles.textFieldLabel}>
                    1. Review Committee Remarks
                  </span>
                </div>

                {/* Markdown Editor */}
                <CansoEditor
                  key={editorResetKey}
                  id="fic-editor"
                  markdown={commiteeRemarks}
                  onChange={(value) => setCommiteeRemarks(value || "")}
                  readOnly={false}
                  className="border border-gray-300 rounded-md"
                  contentEditableClassName="h-[250px]"
                />

                <div className={styles.dividerLine}></div>

                {/* Text Container */}
                <div className={styles.textFieldContainer}>
                  <span className={styles.textFieldLabel}>
                    2. Fraud Classification
                  </span>
                </div>

                <SelectionContainer
                  title="Fraud Classification"
                  items={fraudClassificationOptions.map((option) => ({
                    text: option,
                  }))}
                  selectedItemText={fraudClassification === 'NO_FRAUD' ? 'No Fraud' : fraudClassification}
                  onItemSelect={(value) => {
                    if (value === 'No Fraud') {
                      setFraudClassification('NO_FRAUD');
                    } else {
                      setFraudClassification(value);
                    }
                  }}
                />

                <div className={styles.dividerLine}></div>

                <div className={styles.textFieldContainer}>
                  <span className={styles.textFieldLabel}>
                    3. Select the parties involved in Fraud
                  </span>
                </div>

                {isPartiesLoading ? (
                  <PartiesInvolvedSkeletonLoader />
                ) : (
                  parties.map((party) => (
                    <FICPartyInvolvedCard
                      key={party.party_id}
                      partyName={party.name}
                      partyType={party.party_type}
                      fraudStatus={
                        partyChanges[party.party_id]?.is_fraud !== undefined
                          ? partyChanges[party.party_id].is_fraud
                            ? "fraud"
                            : "not-fraud"
                          : party.is_fraud === true
                            ? "fraud"
                            : party.is_fraud === false
                              ? "not-fraud"
                              : undefined
                      }
                      generateRO={
                        partyChanges[party.party_id]?.generate_ro !== undefined
                          ? partyChanges[party.party_id].generate_ro
                          : party.generate_ro
                      }
                      generateRODisabled={party.generate_ro === true}
                      onFraudStatusChange={(value) =>
                        handleFraudStatusChange(party.party_id, value)
                      }
                      onGenerateROChange={(checked) =>
                        handleGenerateROChange(party.party_id, checked)
                      }
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className={styles.redirectActionsBar}>
            <RedirectActionsBar
              primaryAction={{
                text: "View Show Cause Notice",
                url: getCaseRoutePath(case_id!, CaseRouteId.REPORT_SCN),
                openInNewTab: true,
                disabled: false,
              }}
              secondaryAction={{
                text: "View Reasoned Order",
                url: getCaseRoutePath(case_id!, CaseRouteId.REPORT_RO),
                openInNewTab: true,
                disabled: false,
              }}
            />
          </div>
          {!isFicLoading && case_id && (
            <div className="w-full mx-auto max-w-[1031px]">
              <CommentMainPage
                caseId={case_id}
                threadType={CommentThreadType.FIC}
                entityId={case_id}
                entityType={CommentEntityType.CASE}
                className="w-full max-w-none mx-0 mt-4"
              />
            </div>
          )}
          <div className="h-14" />
        </div>
      </>
    );
}
